import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { GenericResponse } from '../../models/global';
import { environment } from '../../../environments/environment';

export type ApiParams =
  HttpParams | Record<string, string | number | boolean | readonly (string | number | boolean)[]>;

export type ApiHeaders = HttpHeaders | Record<string, string | string[]>;

export interface ApiRequestOptions {
  params?: ApiParams;
  context?: HttpContext;
  headers?: ApiHeaders;
}

const ABSOLUTE_URL = /^https?:\/\//i;

export function unwrapResponse<T>(body: GenericResponse<T>): T {
  return body.response;
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);

  get<T>(path: string, body?: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.request<T>('GET', path, body, options);
  }

  post<T>(path: string, body?: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.request<T>('POST', path, body, options);
  }

  put<T>(path: string, body?: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.request<T>('PUT', path, body, options);
  }

  patch<T>(path: string, body?: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.request<T>('PATCH', path, body, options);
  }

  delete<T>(path: string, body?: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.request<T>('DELETE', path, body, options);
  }

  buildUrl(path: string): string {
    if (ABSOLUTE_URL.test(path)) {
      return path;
    }
    const base = environment.apiUrl.replace(/\/+$/, '');
    return path.startsWith('/') ? `${base}${path}` : `${base}/${path}`;
  }

  private request<T>(
    method: string,
    path: string,
    body?: unknown,
    options?: ApiRequestOptions
  ): Observable<T> {
    return this.http.request<T>(method, this.buildUrl(path), {
      body,
      params: options?.params,
      context: options?.context,
      headers: options?.headers,
    });
  }
}
